import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, IconButton, Button, Divider, LinearProgress } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
    ArrowBackOutlined, LocalAtmOutlined, CheckCircleOutline,
    LockOutlined, NorthEastOutlined, SouthWestOutlined,
    TimerOutlined, PercentOutlined, AccountBalanceOutlined,
} from '@mui/icons-material';

const B = {
    deep:    '#0D0820',
    dark:    '#1A0F3D',
    mid:     '#2D1B69',
    purple:  '#7B5EA7',
    light:   '#9B7EC8',
    lavender:'#F7F4FC',
    ink:     '#0D0A1A',
    green:   '#00C896',
    greenDim:'#00C89618',
    red:     '#FF4D6A',
    redDim:  '#FF4D6A18',
    gold:    '#F5C842',
    orange:  '#FF9500',
};

const LOAN_PLANS = [
    { id: 'quick',    label: 'Quick Cash',   amount: 50000,   duration: '7 days',   rate: '2%',  color: B.green,  description: 'Short-term emergency funds' },
    { id: 'standard', label: 'Standard',     amount: 200000,  duration: '30 days',  rate: '5%',  color: B.purple, description: 'Medium-term personal loan', popular: true },
    { id: 'premium',  label: 'Premium',      amount: 1000000, duration: '90 days',  rate: '8%',  color: B.gold,   description: 'Large-scale financing' },
    { id: 'max',      label: 'Max Credit',   amount: 5000000, duration: '180 days', rate: '12%', color: '#FF6B35', description: 'Maximum eligible amount' },
];

const QUICK_AMOUNTS = [10000, 20000, 50000, 100000, 200000, 500000];

const ACTIVE_LOAN = {
    amount: 50000,
    remaining: 35000,
    dueDate: '15 Apr 2026',
    monthlyPayment: 17500,
    plan: 'Quick Cash',
    status: 'On Track',
};

const LOAN_HISTORY = [
    { id: 1, amount: 50000,  repaid: 50000,  date: 'Dec 2024', status: 'repaid'  },
    { id: 2, amount: 100000, repaid: 100000, date: 'Aug 2024', status: 'repaid'  },
    { id: 3, amount: 20000,  repaid: 20000,  date: 'Mar 2024', status: 'repaid'  },
];

const fmt = (n: number) =>
    new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(n);

const LoansPage: React.FC = () => {
    const navigate = useNavigate();
    const theme    = useTheme();
    const isDark   = theme.palette.mode === 'dark';

    const [step,        setStep]        = useState<1 | 2 | 3>(1);
    const [selectedPlan, setSelectedPlan] = useState('');
    const [amount,      setAmount]      = useState('');
    const [purpose,     setPurpose]     = useState('');
    const [loading,     setLoading]     = useState(false);
    const [done,        setDone]        = useState(false);

    const numAmount   = parseFloat(amount) || 0;
    const creditScore = 653;
    const maxLoan     = 500000;
    const plan        = LOAN_PLANS.find(p => p.id === selectedPlan);

    const pageBg     = isDark ? B.deep : B.lavender;
    const surfaceBg  = isDark ? alpha(B.mid, .55) : '#fff';
    const surfaceBdr = `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`;
    const surfaceShd = `0 4px 28px ${alpha(B.dark, isDark ? .25 : .06)}`;
    const textPri    = isDark ? '#fff' : B.ink;
    const textMid    = alpha(isDark ? '#fff' : B.ink, .45);
    const textFaint  = alpha(isDark ? '#fff' : B.ink, .28);
    const dividerC   = alpha(isDark ? '#fff' : B.dark, .08);

    const glassCard = {
        background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd,
        borderRadius: '20px', backdropFilter: isDark ? 'blur(20px)' : 'none',
    };

    const handleApply = () => {
        setLoading(true);
        setTimeout(() => { setLoading(false); setDone(true); }, 2000);
    };

    const goBack = () => {
        if (step === 1) navigate('/wallet');
        else setStep(s => (s - 1) as any);
    };

    const PURPOSES = ['Business', 'Education', 'Emergency', 'Medical', 'Travel', 'Home Improvement'];

    /* ── SUCCESS ── */
    if (done) return (
        <Box sx={{ minHeight: '100vh', background: isDark ? `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${B.purple} 100%)` : B.lavender, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 3 }}>
            <Box sx={{ textAlign: 'center', maxWidth: 340, mx: 'auto' }}>
                <Box sx={{ width: 80, height: 80, borderRadius: '50%', background: B.greenDim, border: `2px solid ${B.green}`, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
                    <CheckCircleOutline sx={{ color: B.green, fontSize: 40 }} />
                </Box>
                <Typography sx={{ color: textPri, fontFamily: "'Instrument Serif', serif", fontSize: '1.9rem', mb: 1 }}>Loan Approved!</Typography>
                <Typography sx={{ color: textMid, fontSize: '.85rem', mb: .5 }}>{fmt(numAmount)} has been sent to your wallet</Typography>
                <Typography sx={{ color: textFaint, fontSize: '.78rem', mb: 1 }}>Repay by {plan?.duration} · {plan?.rate} interest</Typography>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: .75, background: alpha(B.green, .1), border: `1px solid ${alpha(B.green, .2)}`, borderRadius: 20, px: 2, py: .6, mb: 4 }}>
                    <Typography sx={{ color: B.green, fontSize: '.72rem', fontWeight: 700 }}>Disbursed instantly to wallet</Typography>
                </Box>
                <Box display="flex" gap={2} justifyContent="center">
                    <Button onClick={() => navigate('/wallet')} variant="contained"
                            sx={{ background: `linear-gradient(135deg, ${B.purple}, ${B.light})`, borderRadius: 50, px: 4, py: 1.3, textTransform: 'none', fontWeight: 700, fontSize: '.9rem', boxShadow: `0 12px 32px ${alpha(B.purple, .4)}` }}>
                        Back to Wallet
                    </Button>
                </Box>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ minHeight: '100vh', background: pageBg, pb: 10, fontFamily: "'DM Sans', system-ui, sans-serif" }}>

            {/* ══ HERO ══ */}
            <Box sx={{
                background: `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${B.purple} 100%)`,
                mt: { xs: '-76px', sm: 0 },
                pt: { xs: '80px', sm: '28px' },
                pb: { xs: 4, sm: 14 }, px: 3, position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -70, right: -50, width: 240, height: 240, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.light, .18)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: -10, left: -40, width: 200, height: 200, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.gold, .08)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

                <Box display="flex" alignItems="center" gap={1.5}>
                    <IconButton onClick={goBack}
                                sx={{ color: alpha('#fff', .7), background: alpha('#fff', .1), border: `1px solid ${alpha('#fff', .15)}`, width: 36, height: 36, '&:hover': { background: alpha('#fff', .2) } }}>
                        <ArrowBackOutlined sx={{ fontSize: 17 }} />
                    </IconButton>
                    <Box flex={1}>
                        <Typography sx={{ color: alpha('#fff', .35), fontSize: '.62rem', letterSpacing: '.18em', textTransform: 'uppercase' }}>
                            {step === 1 ? 'NG Credit' : step === 2 ? 'Step 1 of 2' : 'Step 2 of 2'}
                        </Typography>
                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem', letterSpacing: '-.025em', fontFamily: "'Instrument Serif', serif" }}>
                            Instant Loans
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: .5, background: alpha(B.green, .15), border: `1px solid ${alpha(B.green, .3)}`, borderRadius: 20, px: 1.5, py: .5 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: B.green, boxShadow: `0 0 6px ${B.green}` }} />
                        <Typography sx={{ color: B.green, fontSize: '.68rem', fontWeight: 700 }}>ELIGIBLE</Typography>
                    </Box>
                </Box>
            </Box>

            <Container maxWidth="sm" sx={{ mt: { xs: 2, sm: -8 }, position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>

                {/* ── OVERVIEW (step 1) ── */}
                {step === 1 && (
                    <>
                        {/* Active loan card */}
                        <Box sx={{ ...glassCard, p: 2.5, mb: 2, border: `1px solid ${alpha(B.green, .2)}` }}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                <Box>
                                    <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', fontWeight: 600, mb: .5 }}>Active Loan</Typography>
                                    <Typography sx={{ color: textPri, fontFamily: "'Instrument Serif', serif", fontSize: '1.8rem', fontWeight: 700 }}>{fmt(ACTIVE_LOAN.amount)}</Typography>
                                    <Typography sx={{ color: textFaint, fontSize: '.72rem' }}>{ACTIVE_LOAN.plan}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: .5, background: alpha(B.green, .1), border: `1px solid ${alpha(B.green, .25)}`, borderRadius: 20, px: 1.25, py: .4 }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: B.green, boxShadow: `0 0 6px ${B.green}` }} />
                                    <Typography sx={{ color: B.green, fontSize: '.65rem', fontWeight: 700 }}>{ACTIVE_LOAN.status}</Typography>
                                </Box>
                            </Box>

                            <Typography sx={{ color: textFaint, fontSize: '.65rem', fontWeight: 600, mb: 1 }}>Repayment progress</Typography>
                            <LinearProgress variant="determinate"
                                            value={((ACTIVE_LOAN.amount - ACTIVE_LOAN.remaining) / ACTIVE_LOAN.amount) * 100}
                                            sx={{ height: 8, borderRadius: 4, background: alpha(B.purple, .1), '& .MuiLinearProgress-bar': { background: `linear-gradient(90deg, ${B.purple}, ${B.green})`, borderRadius: 4 } }} />
                            <Box display="flex" justifyContent="space-between" mt={1}>
                                <Typography sx={{ color: textFaint, fontSize: '.68rem' }}>Remaining: <span style={{ color: textPri, fontWeight: 700 }}>{fmt(ACTIVE_LOAN.remaining)}</span></Typography>
                                <Typography sx={{ color: textFaint, fontSize: '.68rem' }}>Due: <span style={{ color: B.orange, fontWeight: 700 }}>{ACTIVE_LOAN.dueDate}</span></Typography>
                            </Box>

                            <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${dividerC}`, display: 'flex', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography sx={{ color: textFaint, fontSize: '.62rem', mb: .25 }}>Monthly payment</Typography>
                                    <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.9rem' }}>{fmt(ACTIVE_LOAN.monthlyPayment)}</Typography>
                                </Box>
                                <Button size="small" variant="contained"
                                        sx={{ background: `linear-gradient(135deg, ${B.purple}, ${B.light})`, borderRadius: 20, px: 2, textTransform: 'none', fontWeight: 700, fontSize: '.75rem', boxShadow: 'none' }}>
                                    Repay Now
                                </Button>
                            </Box>
                        </Box>

                        {/* Credit score eligibility */}
                        <Box sx={{ ...glassCard, p: 2.5, mb: 2 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                                <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.9rem' }}>Your Eligibility</Typography>
                                <Button size="small" onClick={() => navigate('/dashboard')}
                                        sx={{ color: B.purple, fontWeight: 600, fontSize: '.7rem', textTransform: 'none', px: 1, '&:hover': { background: alpha(B.purple, .07) } }}>
                                    Improve →
                                </Button>
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5 }}>
                                {[
                                    { label: 'Credit Score', value: `${creditScore}`, sub: '/ 850', color: B.purple },
                                    { label: 'Max Loan',     value: '₦500k',          sub: 'available', color: B.green },
                                    { label: 'Good Loans',   value: '3',              sub: 'repaid',    color: B.gold },
                                ].map(s => (
                                    <Box key={s.label} sx={{ p: 1.5, borderRadius: '14px', background: alpha(s.color, isDark ? .1 : .05), border: `1px solid ${alpha(s.color, .18)}`, textAlign: 'center' }}>
                                        <Typography sx={{ color: s.color, fontFamily: "'Instrument Serif', serif", fontSize: '1.3rem', fontWeight: 700 }}>{s.value}</Typography>
                                        <Typography sx={{ color: textFaint, fontSize: '.58rem' }}>{s.sub}</Typography>
                                        <Typography sx={{ color: textMid, fontSize: '.62rem', fontWeight: 600, mt: .25 }}>{s.label}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        <Button fullWidth onClick={() => setStep(2)}
                                sx={{
                                    py: 1.65, borderRadius: '14px', textTransform: 'none', fontWeight: 700, fontSize: '.95rem',
                                    background: `linear-gradient(135deg, ${B.purple}, ${B.light})`,
                                    color: '#fff', boxShadow: `0 12px 32px ${alpha(B.purple, .35)}`,
                                    mb: 2,
                                    '&:hover': { boxShadow: `0 16px 40px ${alpha(B.purple, .5)}` },
                                }}>
                            Apply for New Loan
                        </Button>

                        {/* Loan history */}
                        <Box sx={{ ...glassCard, overflow: 'hidden' }}>
                            <Box sx={{ px: 2.5, pt: 2.5, pb: 2 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '.95rem', color: textPri, letterSpacing: '-.02em' }}>Loan History</Typography>
                            </Box>
                            {LOAN_HISTORY.map((l, i) => (
                                <React.Fragment key={l.id}>
                                    <Box sx={{ px: 2.5, py: 1.75, display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Box sx={{ width: 40, height: 40, borderRadius: 2.5, background: B.greenDim, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                            <CheckCircleOutline sx={{ color: B.green, fontSize: 18 }} />
                                        </Box>
                                        <Box flex={1}>
                                            <Typography sx={{ color: textPri, fontWeight: 600, fontSize: '.84rem' }}>{fmt(l.amount)}</Typography>
                                            <Typography sx={{ color: textFaint, fontSize: '.68rem' }}>Repaid in full · {l.date}</Typography>
                                        </Box>
                                        <Box sx={{ background: alpha(B.green, .1), border: `1px solid ${alpha(B.green, .2)}`, borderRadius: 8, px: 1, py: .3 }}>
                                            <Typography sx={{ color: B.green, fontSize: '.62rem', fontWeight: 700 }}>REPAID</Typography>
                                        </Box>
                                    </Box>
                                    {i < LOAN_HISTORY.length - 1 && <Divider sx={{ mx: 2.5, borderColor: dividerC }} />}
                                </React.Fragment>
                            ))}
                        </Box>
                    </>
                )}

                {/* ── STEP 2: Pick plan + amount ── */}
                {step === 2 && (
                    <>
                        {/* Loan plans */}
                        <Box sx={{ ...glassCard, p: 2.5, mb: 2 }}>
                            <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', fontWeight: 600, mb: 2 }}>Choose Plan</Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                                {LOAN_PLANS.map(p => (
                                    <Box key={p.id} onClick={() => setSelectedPlan(p.id)} sx={{
                                        p: 2, borderRadius: '16px', cursor: 'pointer', position: 'relative',
                                        background: selectedPlan === p.id ? alpha(p.color, isDark ? .12 : .06) : 'transparent',
                                        border: `1.5px solid ${selectedPlan === p.id ? alpha(p.color, .4) : dividerC}`,
                                        transition: 'all .18s',
                                    }}>
                                        {p.popular && (
                                            <Box sx={{ position: 'absolute', top: -1, right: 12, background: `linear-gradient(135deg, ${B.purple}, ${B.light})`, borderRadius: '0 0 8px 8px', px: 1.25, py: .3 }}>
                                                <Typography sx={{ color: '#fff', fontSize: '.55rem', fontWeight: 700, letterSpacing: '.05em' }}>MOST POPULAR</Typography>
                                            </Box>
                                        )}
                                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                            <Box>
                                                <Typography sx={{ color: selectedPlan === p.id ? p.color : textPri, fontWeight: 700, fontSize: '.9rem' }}>{p.label}</Typography>
                                                <Typography sx={{ color: textFaint, fontSize: '.72rem', mt: .25 }}>{p.description}</Typography>
                                            </Box>
                                            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                                <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.85rem' }}>Up to {fmt(p.amount)}</Typography>
                                                <Typography sx={{ color: textFaint, fontSize: '.7rem' }}>{p.duration} · {p.rate} interest</Typography>
                                            </Box>
                                        </Box>
                                        {selectedPlan === p.id && (
                                            <Box sx={{ display: 'flex', gap: 2, mt: 1.5, pt: 1.5, borderTop: `1px solid ${alpha(p.color, .15)}` }}>
                                                {[
                                                    { Icon: TimerOutlined,          label: p.duration, sub: 'Duration' },
                                                    { Icon: PercentOutlined,        label: p.rate,     sub: 'Interest' },
                                                    { Icon: AccountBalanceOutlined, label: 'Instant',  sub: 'Disbursal' },
                                                ].map(f => (
                                                    <Box key={f.sub} sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: .75 }}>
                                                        <f.Icon sx={{ color: p.color, fontSize: 14 }} />
                                                        <Box>
                                                            <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.72rem' }}>{f.label}</Typography>
                                                            <Typography sx={{ color: textFaint, fontSize: '.6rem' }}>{f.sub}</Typography>
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        {/* Amount input */}
                        <Box sx={{ ...glassCard, p: 2.5, mb: 2 }}>
                            <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', fontWeight: 600, mb: 1.5 }}>Loan Amount</Typography>
                            <Box display="flex" alignItems="center" gap={1}>
                                <Typography sx={{ color: textMid, fontSize: '1.5rem', fontWeight: 300, fontFamily: "'Instrument Serif', serif" }}>₦</Typography>
                                <input type="number" placeholder="0" value={amount} onChange={e => setAmount(e.target.value)}
                                       style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: textPri, fontSize: '2rem', fontWeight: 700, fontFamily: "'Instrument Serif', serif" }} />
                            </Box>
                            <Box sx={{ mt: 1, height: '1px', background: `linear-gradient(90deg, ${B.purple}, transparent)` }} />
                            <Typography sx={{ color: textFaint, fontSize: '.7rem', mt: 1 }}>Max: {fmt(maxLoan)}</Typography>

                            <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', fontWeight: 600, mt: 2.5, mb: 1.5 }}>Quick Select</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {QUICK_AMOUNTS.map(q => (
                                    <Box key={q} onClick={() => setAmount(String(q))} sx={{
                                        px: 1.5, py: .6, borderRadius: '20px', cursor: 'pointer', fontSize: '.75rem', fontWeight: 600,
                                        background: amount === String(q) ? alpha(B.purple, .15) : alpha(isDark ? '#fff' : B.dark, .05),
                                        color: amount === String(q) ? B.purple : textMid,
                                        border: `1px solid ${amount === String(q) ? alpha(B.purple, .35) : dividerC}`,
                                        transition: 'all .15s',
                                    }}>
                                        {fmt(q)}
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        {/* Purpose */}
                        <Box sx={{ ...glassCard, p: 2.5, mb: 2 }}>
                            <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', fontWeight: 600, mb: 1.5 }}>Purpose</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {PURPOSES.map(p => (
                                    <Box key={p} onClick={() => setPurpose(p)} sx={{
                                        px: 1.75, py: .7, borderRadius: '20px', cursor: 'pointer', fontSize: '.78rem', fontWeight: 600,
                                        background: purpose === p ? alpha(B.purple, .12) : alpha(isDark ? '#fff' : B.dark, .05),
                                        color: purpose === p ? B.light : textMid,
                                        border: `1px solid ${purpose === p ? alpha(B.purple, .35) : dividerC}`,
                                        transition: 'all .15s',
                                    }}>
                                        {p}
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        <Button fullWidth disabled={!selectedPlan || !numAmount || !purpose} onClick={() => setStep(3)}
                                sx={{
                                    py: 1.65, borderRadius: '14px', textTransform: 'none', fontWeight: 700, fontSize: '.95rem',
                                    background: `linear-gradient(135deg, ${B.purple}, ${B.light})`,
                                    color: '#fff', boxShadow: `0 12px 32px ${alpha(B.purple, .35)}`,
                                    '&:disabled': { opacity: .35, boxShadow: 'none' },
                                }}>
                            Review Application
                        </Button>
                    </>
                )}

                {/* ── STEP 3: Confirm ── */}
                {step === 3 && (
                    <>
                        <Box sx={{ ...glassCard, p: 2.5, mb: 2 }}>
                            <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', fontWeight: 600, mb: 2.5 }}>Loan Summary</Typography>

                            {/* Amount hero */}
                            <Box sx={{ textAlign: 'center', mb: 2.5, p: 2, borderRadius: '16px', background: alpha(B.purple, isDark ? .1 : .05), border: `1px solid ${alpha(B.purple, .15)}` }}>
                                <Typography sx={{ color: textFaint, fontSize: '.68rem', mb: .5 }}>You're borrowing</Typography>
                                <Typography sx={{ color: textPri, fontFamily: "'Instrument Serif', serif", fontSize: '2.4rem', fontWeight: 700 }}>{fmt(numAmount)}</Typography>
                                <Typography sx={{ color: textFaint, fontSize: '.75rem' }}>via {plan?.label} plan</Typography>
                            </Box>

                            {[
                                { label: 'Duration',     value: plan?.duration ?? '' },
                                { label: 'Interest Rate', value: plan?.rate ?? ''    },
                                { label: 'Total Repay',  value: fmt(numAmount * (1 + parseFloat(plan?.rate ?? '0') / 100)) },
                                { label: 'Purpose',      value: purpose              },
                                { label: 'Disbursal',    value: 'Instant to wallet'  },
                            ].map((row, i, arr) => (
                                <React.Fragment key={row.label}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" py={1.25}>
                                        <Typography sx={{ color: textFaint, fontSize: '.8rem' }}>{row.label}</Typography>
                                        <Typography sx={{ color: row.label === 'Total Repay' ? B.orange : textPri, fontWeight: 600, fontSize: '.85rem' }}>{row.value}</Typography>
                                    </Box>
                                    {i < arr.length - 1 && <Divider sx={{ borderColor: dividerC }} />}
                                </React.Fragment>
                            ))}

                            <Box sx={{ mt: 2, p: 1.5, borderRadius: '12px', background: alpha(B.purple, .07), border: `1px solid ${alpha(B.purple, .18)}` }}>
                                <Typography sx={{ color: textMid, fontSize: '.72rem', lineHeight: 1.5, textAlign: 'center' }}>
                                    By confirming, you agree to the loan terms. Failure to repay affects your credit score.
                                </Typography>
                            </Box>
                        </Box>

                        <Button fullWidth onClick={handleApply} disabled={loading}
                                sx={{
                                    py: 1.65, borderRadius: '14px', textTransform: 'none', fontWeight: 700, fontSize: '.95rem',
                                    background: `linear-gradient(135deg, ${B.purple}, ${B.light})`,
                                    color: '#fff', boxShadow: `0 12px 32px ${alpha(B.purple, .35)}`,
                                    '&:disabled': { opacity: .5 },
                                }}>
                            {loading ? 'Processing application…' : `Confirm & Get ${fmt(numAmount)}`}
                        </Button>
                    </>
                )}

                {/* footer */}
                <Box sx={{ mt: 3.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .75 }}>
                    <LockOutlined sx={{ fontSize: 11, color: alpha(isDark ? '#fff' : B.ink, .18) }} />
                    <Typography sx={{ fontSize: '.62rem', color: alpha(isDark ? '#fff' : B.ink, .18), letterSpacing: '.05em' }}>
                        256-BIT ENCRYPTION · NG CREDIT FINANCIAL SERVICES
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default LoansPage;